import React, { useEffect, useState } from 'react';

/**
 * A one-line notice at the bottom of the app ("Switched to Monte Carlo.").
 * Call showInkToast from anywhere; AppShell renders <InkToast />. It clears
 * itself after a few seconds or on Dismiss. An action ("Undo") shows as a
 * button beside the text and closes the notice when used.
 */

interface ToastAction {
  label: string;
  onClick: () => void;
}
type Listener = (text: string, action?: ToastAction) => void;
const listeners = new Set<Listener>();

export function showInkToast(text: string, action?: ToastAction) {
  listeners.forEach((fn) => fn(text, action));
}

export function InkToast() {
  const [text, setText] = useState('');
  const [action, setAction] = useState<ToastAction | undefined>();
  const [key, setKey] = useState(0);

  useEffect(() => {
    const fn: Listener = (t, a) => {
      setText(t);
      setAction(a ? { ...a } : undefined);
      setKey((k) => k + 1);
    };
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  useEffect(() => {
    if (!text) return;
    // A notice with an action stays a little longer, so there's time to use it.
    const t = setTimeout(() => setText(''), action ? 8000 : 4500);
    return () => clearTimeout(t);
  }, [text, key, action]);

  if (!text) return null;
  return (
    <div className="ink-toast" role="status">
      {text}
      {action ? (
        <button
          type="button"
          className="ink-toast-action"
          onClick={() => {
            setText('');
            action.onClick();
          }}
        >
          {action.label}
        </button>
      ) : null}
      <button type="button" onClick={() => setText('')}>
        Dismiss
      </button>
    </div>
  );
}
